/**
 * GA4 Analytics Core Service
 * Provides initialization and tracking functions for Google Analytics 4
 */

import type {
  GA4Config,
  CustomEventParams,
  PageViewParams,
  UserProperties,
  LoginEventParams,
  SearchEventParams,
  SelectContentParams,
  AffiliateClickParams,
  FlightResultParams,
  ErrorEventParams,
} from './types';
import { __DEV__ } from '../utils/utils';

let isInitialized = false;
let measurementId: string | null = null;

function log(..._args: unknown[]) {
  // temporarily disabled
  // if (__DEV__ || __DEV_OR_STAGE__) {
  //   console.log('[GA4]', ...args);
  // }
}

/**
 * Push event to dataLayer for GTM processing
 */
function pushToDataLayer(event: string, params?: Record<string, unknown>) {
  if (!isInitialized) {
    log('GA4 not initialized, skipping:', { event, ...params });
    return;
  }

  window.dataLayer.push({
    event,
    ...params,
  });
}

/**
 * Initialize GA4 with the measurement ID
 */
export function initGA4(config: GA4Config): void {
  if (isInitialized) {
    log('GA4 already initialized');
    return;
  }

  if (!config.measurementId) {
    return;
  }

  measurementId = config.measurementId;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: config.sendPageViews ?? false,
    debug_mode: __DEV__,
  });

  isInitialized = true;
  log('GA4 initialized with ID:', measurementId);
}

/**
 * Check if GA4 is initialized
 */
export function isGA4Initialized(): boolean {
  return isInitialized;
}

/**
 * Returns the current GA4 session id from the `_ga_<container-id>` cookie
 * (format `GS1.1.<session_id>.<...>`). Returns `""` if not available yet.
 */
export function getGaSessionId(): string {
  if (typeof document === "undefined") return "";
  const containerId = measurementId?.replace(/^G-/, "") ?? "";
  if (!containerId) return "";
  const cookieName = `_ga_${containerId}`;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${cookieName}=`));
  if (!match) return "";
  const value = match.slice(cookieName.length + 1);
  // GA4 format: `GS1.1.<session_id>.<session_count>.<is_engaged>.<engagement_time>.<is_first_session>...`
  const parts = value.split(".");
  return parts[2] ?? "";
}

/**
 * Track a page view
 */
export function trackPageView(params?: PageViewParams): void {
  const pageParams = {
    page_path: params?.page_path ?? window.location.pathname,
    page_title: params?.page_title ?? document.title,
    page_location: params?.page_location ?? window.location.href,
  };

  pushToDataLayer('page_view', pageParams);
  log('Page view:', pageParams);
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, params?: CustomEventParams): void {
  pushToDataLayer(eventName, params);
  log('Event:', eventName, params);
}

/**
 * Set user properties
 */
export function setUserProperties(properties: UserProperties): void {
  if (!isInitialized) {
    log('GA4 not initialized, skipping user properties:', properties);
    return;
  }

  window.dataLayer.push({
    event: 'set_user_properties',
    user_properties: properties,
  });
  log('User properties set:', properties);
}

/**
 * Set user ID for cross-device tracking
 */
export function setUserId(userId: string | null): void {
  if (!isInitialized) {
    log('GA4 not initialized, skipping user ID');
    return;
  }

  if (userId) {
    window.dataLayer.push({
      event: 'set_user_id',
      user_id: userId,
    });
    log('User ID set:', userId);
  }
}

// ============================================
// Pre-defined Event Tracking Functions
// ============================================

/**
 * Track login event
 */
export function trackLogin(params?: LoginEventParams): void {
  trackEvent('login', {
    event_category: 'authentication',
    ...params,
  });
}

/**
 * Track logout event
 */
export function trackLogout(): void {
  trackEvent('logout', {
    event_category: 'authentication',
  });
}

/**
 * Track signup/registration event
 */
export function trackSignup(method?: string): void {
  trackEvent('sign_up', {
    event_category: 'authentication',
    method,
  });
}

/**
 * Track flight search
 */
export function trackFlightSearch(params: SearchEventParams): void {
  trackEvent('search', {
    event_category: 'search',
    ...params,
  });
}

/**
 * Track content selection (button clicks, card selections, etc.)
 */
export function trackSelectContent(params: SelectContentParams): void {
  trackEvent('select_content', {
    event_category: 'engagement',
    ...params,
  });
}

/**
 * Track affiliate click (external link click)
 */
export function trackAffiliateClick(params: AffiliateClickParams): void {
  trackEvent('affiliate_click', {
    event_category: 'conversion',
    ...params,
  });
}

/**
 * Track flight result view
 */
export function trackFlightResultView(params: FlightResultParams): void {
  trackEvent('view_item', {
    event_category: 'engagement',
    content_type: 'flight',
    ...params,
  });
}

/**
 * Track flight result selection
 */
export function trackFlightResultSelect(params: FlightResultParams): void {
  trackEvent('select_item', {
    event_category: 'engagement',
    content_type: 'flight',
    ...params,
  });
}

/**
 * Track modal open
 */
export function trackModalOpen(modalName: string): void {
  trackEvent('modal_open', {
    event_category: 'engagement',
    event_label: modalName,
  });
}

/**
 * Track modal close
 */
export function trackModalClose(modalName: string): void {
  trackEvent('modal_close', {
    event_category: 'engagement',
    event_label: modalName,
  });
}

/**
 * Track error
 */
export function trackError(params: ErrorEventParams): void {
  trackEvent('error', {
    event_category: 'error',
    ...params,
  });
}

/**
 * Track CTA button click
 */
export function trackCTAClick(buttonName: string, location?: string): void {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: buttonName,
    button_location: location,
  });
}

/**
 * Track hero card click
 */
export function trackHeroCardClick(cardType: string): void {
  trackEvent('hero_card_click', {
    event_category: 'navigation',
    event_label: cardType,
  });
}

/**
 * Track external link click
 */
export function trackExternalLinkClick(url: string, linkName?: string): void {
  trackEvent('external_link_click', {
    event_category: 'navigation',
    event_label: linkName ?? url,
    destination_url: url,
  });
}
